import { PartyStages } from 'services/api/getPartyStages';

/** Call status string values. See backend for Constant names (e.g. CallStatusPending). */
export const CallType = {
    // General / in-progress
    PENDING: 'PENDING', // Internal status
    QUEUED: 'QUEUED', // Internal status
    DIALING: 'DIALING',
    RINGING: 'RINGING',
    IN_PROGRESS: 'IN_PROGRESS', // AI agent on the call
    TRANSFER_IN_PROGRESS: 'TRANSFER_IN_PROGRESS', // Transferring to an agent
    HUMAN_ON_CALL: 'HUMAN_ON_CALL', // Human agent on the call

    // Terminal
    AGENT_ABANDONED: 'AGENT_ABANDONED', // Agent ended the call
    USER_ABANDONED: 'USER_ABANDONED', // User ended the call
    FAILED: 'FAILED', // Voice agent failure
    BUSY: 'BUSY', // User rejected / busy
    NO_ANSWER: 'NO_ANSWER', // User unavailable
    VOICEMAIL: 'VOICEMAIL', // Left voicemail
    VOICEMAIL_FAILED: 'VOICEMAIL_FAILED', // Voicemail send failed / full
    TRANSFER_FAILED: 'TRANSFER_FAILED', // Transfer to agent failed
    TRANSFER_COMPLETED: 'TRANSFER_COMPLETED', // Transfer completed
    TRANSFER_USER_ABANDONED: 'TRANSFER_USER_ABANDONED', // Abandoned during transfer
    NO_AGENTS_AVAILABLE: 'NO_AGENTS_AVAILABLE', // No agents available
    BAD_NUMBER: 'BAD_NUMBER', // Bad number
    COMPLETED: 'COMPLETED', // Call completed
    BLOCKED: 'BLOCKED', // Call blocked

    // Inbound-specific
    AI_SHOULD_CONNECT: 'AI_SHOULD_CONNECT', // Inbound placeholder until AI connects
    TRANSFER_CALL_CENTER_IN_PROGRESS: 'TRANSFER_CALL_CENTER_IN_PROGRESS',
    TRANSFER_CALL_CENTER_COMPLETED: 'TRANSFER_CALL_CENTER_COMPLETED',
    TRANSFER_CALL_CENTER_FAILED: 'TRANSFER_CALL_CENTER_FAILED',
    AGENT_REJECTED: 'AGENT_REJECTED',

    // Manual-specific
    MANUAL_PENDING: 'MANUAL_PENDING',
    MANUAL_IN_PROGRESS: 'MANUAL_IN_PROGRESS',
    MANUAL_AGENT_CONNECTED: 'MANUAL_AGENT_CONNECTED',
    MANUAL_AGENT_ABANDONED: 'MANUAL_AGENT_ABANDONED',

    // Fallback value
    unknown: 'unknown',
} as const;

export const CallTypeCategories: Record<Exclude<keyof typeof CallType, 'unknown'>, string> = {
    [CallType.PENDING]: 'General',
    [CallType.QUEUED]: 'General',
    [CallType.DIALING]: 'General',
    [CallType.RINGING]: 'General',
    [CallType.IN_PROGRESS]: 'General',
    [CallType.TRANSFER_IN_PROGRESS]: 'General',
    [CallType.HUMAN_ON_CALL]: 'General',
    [CallType.AGENT_ABANDONED]: 'Terminal',
    [CallType.USER_ABANDONED]: 'Terminal',
    [CallType.FAILED]: 'Terminal',
    [CallType.BUSY]: 'Terminal',
    [CallType.NO_ANSWER]: 'Terminal',
    [CallType.VOICEMAIL]: 'Terminal',
    [CallType.VOICEMAIL_FAILED]: 'Terminal',
    [CallType.TRANSFER_FAILED]: 'Terminal',
    [CallType.TRANSFER_COMPLETED]: 'Terminal',
    [CallType.TRANSFER_USER_ABANDONED]: 'Terminal',
    [CallType.NO_AGENTS_AVAILABLE]: 'Terminal',
    [CallType.BAD_NUMBER]: 'Terminal',
    [CallType.COMPLETED]: 'Terminal',
    [CallType.BLOCKED]: 'Terminal',
    [CallType.AI_SHOULD_CONNECT]: 'Inbound',
    [CallType.TRANSFER_CALL_CENTER_IN_PROGRESS]: 'Inbound',
    [CallType.TRANSFER_CALL_CENTER_COMPLETED]: 'Inbound',
    [CallType.TRANSFER_CALL_CENTER_FAILED]: 'Inbound',
    [CallType.AGENT_REJECTED]: 'Inbound',
    [CallType.MANUAL_PENDING]: 'Manual',
    [CallType.MANUAL_IN_PROGRESS]: 'Manual',
    [CallType.MANUAL_AGENT_CONNECTED]: 'Manual',
    [CallType.MANUAL_AGENT_ABANDONED]: 'Manual',
};

export const TerminalCallType = {
    // Terminal states
    COMPLETED: 'COMPLETED',
    AGENT_ABANDONED: 'AGENT_ABANDONED',
    USER_ABANDONED: 'USER_ABANDONED',
    FAILED: 'FAILED',
    BUSY: 'BUSY',
    BLOCKED: 'BLOCKED',
    NO_ANSWER: 'NO_ANSWER',
    VOICEMAIL: 'VOICEMAIL',
    VOICEMAIL_FAILED: 'VOICEMAIL_FAILED',
    TRANSFER_FAILED: 'TRANSFER_FAILED',
    TRANSFER_COMPLETED: 'TRANSFER_COMPLETED',
    TRANSFER_USER_ABANDONED: 'TRANSFER_USER_ABANDONED',
    NO_AGENTS_AVAILABLE: 'NO_AGENTS_AVAILABLE',
    AGENT_REJECTED: 'AGENT_REJECTED',
    BAD_NUMBER: 'BAD_NUMBER',
} as const;

export type CallStatistic = {
    pending: number;
    overDue: number;
    dialing: number;
    ringing: number;
    inProgress: number;
    transferInProgress: number;
    humanOnCall: number;

    agentAbandoned: number;
    userAbandoned: number;
    failed: number;
    busy: number;
    noAnswer: number;
    voicemail: number;
    voicemailSuccess: number;
    voicemailFailed: number;
    transferFailed: number;
    transferCompleted: number;
    transferUserAbandoned: number;
    noAgentsAvailable: number;
    agentRejected: number;
    total: number;
    transfer: number;
    badNumber: number;
    blocked: number;

    completed: number;
    contacted: number;
};

export const Disposition = {
    QUALIFY: 'QUALIFY',
    UNQUALIFIED: 'UNQUALIFIED',
    UNDETERMINED: 'UNDETERMINED',
    CALLBACK: 'CALLBACK',
    INTERESTED: 'INTERESTED',
    NOT_INTERESTED: 'NOT_INTERESTED',
} as const;

export const CallDirection = {
    INBOUND: 'INBOUND',
    OUTBOUND: 'OUTBOUND',
    ALL: 'ALL',
} as const;

export type CallDirectionType = (typeof CallDirection)[keyof typeof CallDirection];

export type DispositionType = (typeof Disposition)[keyof typeof Disposition];

export type SortDirection = 'asc' | 'desc';

export type SortField = 'callDate' | 'callDuration';

export type CallDataType = {
    callId: string;
    startTime: string;
    endTime: string;
    scheduledDate: string;
    contactName: string;
    phoneNumber: string;
    duration: number;
    status: keyof typeof CallType;
    partyStage: keyof typeof PartyStages;
    contactId: string;
    callDuration: number;
    crmId: string;
    transferTo: string;
    disposition: keyof typeof Disposition;
    callDirection: keyof typeof CallDirection;
    messageDirection?: string;
    overDue: boolean;
    aggregatedTagsUsed: boolean;
};
